import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import { OverlayPanel } from '../ui/OverlayPanel';
import { useGlobalTooltip } from './GlobalTooltip';

interface InventoryItem {
  itemId: string;
  quantity: number;
  definition?: {
    id: string;
    name: string;
    description?: string;
    type?: string;
    rarity?: string;
    icon: string;
    stackable?: boolean;
    maxStack?: number;
  };
}

interface ItemDefinition {
  id: string;
  name: string;
  description?: string;
  type?: string;
  rarity?: string;
  icon: string;
  stackable?: boolean;
  maxStack?: number;
}

interface BankPanelProps {
  socket: Socket | null;
  isOpen: boolean;
  onClose: () => void;
  itemDefinitions: Record<string, ItemDefinition>;
}

const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
};

const renderItemIcon = (icon: string | undefined, name: string): JSX.Element => {
  const publicUrl: string = process.env.PUBLIC_URL || '';
  const resolvedSrc = icon && icon.startsWith('/assets')
    ? `${publicUrl}${icon}`
    : icon && (icon.startsWith('http://') || icon.startsWith('https://'))
    ? icon
    : `${publicUrl}/assets/unknown.png`;
  return (
    <img
      src={resolvedSrc}
      alt={name}
      style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '2px' }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        if (target.src !== `${publicUrl}/assets/unknown.png`) {
          target.onerror = null;
          target.src = `${publicUrl}/assets/unknown.png`;
        }
      }}
    />
  );
};

export const BankPanel: React.FC<BankPanelProps> = ({
  socket,
  isOpen,
  onClose,
  itemDefinitions,
}) => {
  const { showTooltip, hideTooltip } = useGlobalTooltip();
  const [bankItems, setBankItems] = useState<InventoryItem[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [bankMaxSlots, setBankMaxSlots] = useState<number>(100);
  const [inventoryMaxSlots, setInventoryMaxSlots] = useState<number>(30);
  const [bankUsedSlots, setBankUsedSlots] = useState<number>(0);
  const [inventoryUsedSlots, setInventoryUsedSlots] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState<{ itemId: string; source: 'bank' | 'inventory' } | null>(null);
  const [depositQuantity, setDepositQuantity] = useState<number>(1);
  const [withdrawQuantity, setWithdrawQuantity] = useState<number>(1);
  const [error, setError] = useState<string>('');
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; item: InventoryItem; source: 'bank' | 'inventory' } | null>(null);

  // Close context menu on outside click
  useEffect(() => {
    const handleClick = () => {
      setContextMenu(null);
    };

    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => {
        document.removeEventListener('click', handleClick);
      };
    }
  }, [contextMenu]);

  // Load bank contents on open
  useEffect(() => {
    if (isOpen && socket) {
      socket.emit('bank:getContents');
    }
  }, [isOpen, socket]);

  // Listen for bank contents
  useEffect(() => {
    if (!socket) return;

    const handleContents = (data: {
      bankItems: InventoryItem[];
      bankMaxSlots: number;
      bankUsedSlots: number;
      inventoryItems: InventoryItem[];
      inventoryMaxSlots: number;
      inventoryUsedSlots: number;
    }) => {
      setBankItems(data.bankItems);
      setBankMaxSlots(data.bankMaxSlots);
      setBankUsedSlots(data.bankUsedSlots);
      setInventoryItems(data.inventoryItems);
      setInventoryMaxSlots(data.inventoryMaxSlots);
      setInventoryUsedSlots(data.inventoryUsedSlots);
      setError('');
    };

    const handleDepositSuccess = (data: {
      bankItems: InventoryItem[];
      bankUsedSlots: number;
      inventoryItems: InventoryItem[];
      inventoryUsedSlots: number;
    }) => {
      setBankItems(data.bankItems);
      setBankUsedSlots(data.bankUsedSlots);
      setInventoryItems(data.inventoryItems);
      setInventoryUsedSlots(data.inventoryUsedSlots);
      setSelectedItem(null);
      setDepositQuantity(1);
      setError('');
    };

    const handleWithdrawSuccess = (data: {
      bankItems: InventoryItem[];
      bankUsedSlots: number;
      inventoryItems: InventoryItem[];
      inventoryUsedSlots: number;
    }) => {
      setBankItems(data.bankItems);
      setBankUsedSlots(data.bankUsedSlots);
      setInventoryItems(data.inventoryItems);
      setInventoryUsedSlots(data.inventoryUsedSlots);
      setSelectedItem(null);
      setWithdrawQuantity(1);
      setError('');
    };

    const handleDepositAllSuccess = (data: {
      bankItems: InventoryItem[];
      bankUsedSlots: number;
      inventoryItems: InventoryItem[];
      inventoryUsedSlots: number;
    }) => {
      setBankItems(data.bankItems);
      setBankUsedSlots(data.bankUsedSlots);
      setInventoryItems(data.inventoryItems);
      setInventoryUsedSlots(data.inventoryUsedSlots);
      setSelectedItem(null);
      setDepositQuantity(1);
      setError('');
    };

    const handleWithdrawAllSuccess = (data: {
      bankItems: InventoryItem[];
      bankUsedSlots: number;
      inventoryItems: InventoryItem[];
      inventoryUsedSlots: number;
    }) => {
      setBankItems(data.bankItems);
      setBankUsedSlots(data.bankUsedSlots);
      setInventoryItems(data.inventoryItems);
      setInventoryUsedSlots(data.inventoryUsedSlots);
      setSelectedItem(null);
      setWithdrawQuantity(1);
      setError('');
    };

    const handleError = (data: { message: string }) => {
      setError(data.message);
    };

    socket.on('bank:contents', handleContents);
    socket.on('bank:depositSuccess', handleDepositSuccess);
    socket.on('bank:withdrawSuccess', handleWithdrawSuccess);
    socket.on('bank:depositAllSuccess', handleDepositAllSuccess);
    socket.on('bank:withdrawAllSuccess', handleWithdrawAllSuccess);
    socket.on('bank:error', handleError);

    return () => {
      socket.off('bank:contents', handleContents);
      socket.off('bank:depositSuccess', handleDepositSuccess);
      socket.off('bank:withdrawSuccess', handleWithdrawSuccess);
      socket.off('bank:depositAllSuccess', handleDepositAllSuccess);
      socket.off('bank:withdrawAllSuccess', handleWithdrawAllSuccess);
      socket.off('bank:error', handleError);
    };
  }, [socket]);

  const handleDeposit = useCallback(() => {
    if (!socket || !selectedItem || selectedItem.source !== 'inventory') {
      setError('Select an inventory item to deposit');
      return;
    }

    if (depositQuantity <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }

    socket.emit('bank:deposit', {
      itemId: selectedItem.itemId,
      quantity: depositQuantity,
    });
  }, [socket, selectedItem, depositQuantity]);

  const handleWithdraw = useCallback(() => {
    console.log('[BankPanel] handleWithdraw called', { socket: !!socket, selectedItem, withdrawQuantity });
    
    if (!socket) {
      setError('No socket connection');
      return;
    }
    
    if (!selectedItem) {
      setError('Select a bank item to withdraw');
      return;
    }
    
    if (selectedItem.source !== 'bank') {
      setError('Select a bank item to withdraw');
      return;
    }

    if (withdrawQuantity <= 0) {
      setError('Quantity must be greater than 0');
      return;
    }

    console.log('[BankPanel] Emitting bank:withdraw', { itemId: selectedItem.itemId, quantity: withdrawQuantity });
    socket.emit('bank:withdraw', {
      itemId: selectedItem.itemId,
      quantity: withdrawQuantity,
    });
  }, [socket, selectedItem, withdrawQuantity]);

  const handleDepositAll = useCallback(() => {
    if (!socket || !selectedItem || selectedItem.source !== 'inventory') {
      return;
    }

    const item = inventoryItems.find(i => i.itemId === selectedItem.itemId);
    if (!item) return;

    socket.emit('bank:deposit', {
      itemId: selectedItem.itemId,
      quantity: item.quantity,
    });
  }, [socket, selectedItem, inventoryItems]);

  const handleDepositInventory = useCallback(() => {
    if (!socket) return;
    socket.emit('bank:depositAllInventory');
  }, [socket]);

  const handleWithdrawAll = useCallback((itemId?: string) => {
    if (!socket) return;
    
    const targetItemId = itemId || selectedItem?.itemId;
    if (!targetItemId && bankItems.length === 0) return;

    // If no specific item selected, withdraw all items from bank
    if (!targetItemId) {
      socket.emit('bank:withdrawAllItems');
      return;
    }

    socket.emit('bank:withdrawAll', {
      itemId: targetItemId,
    });
  }, [socket, selectedItem, bankItems]);

  const handleQuantityButton = useCallback((quantity: number | 'all', source: 'deposit' | 'withdraw') => {
    if (source === 'deposit') {
      if (quantity === 'all') {
        const item = selectedItem ? inventoryItems.find(i => i.itemId === selectedItem.itemId) : null;
        setDepositQuantity(item?.quantity || 1);
      } else {
        setDepositQuantity(quantity);
      }
    } else {
      if (quantity === 'all') {
        const item = selectedItem ? bankItems.find(i => i.itemId === selectedItem.itemId) : null;
        setWithdrawQuantity(item?.quantity || 1);
      } else {
        setWithdrawQuantity(quantity);
      }
    }
  }, [selectedItem, inventoryItems, bankItems]);

  const getItemDefinition = (item: InventoryItem | null): ItemDefinition | null => {
    if (!item) return null;
    
    // First, check if the item has a definition from the server (for bank items)
    if (item.definition) {
      return {
        id: item.definition.id,
        name: item.definition.name,
        description: item.definition.description || '',
        type: item.definition.type || 'material',
        rarity: item.definition.rarity || 'common',
        icon: item.definition.icon,
        stackable: item.definition.stackable ?? true,
        maxStack: item.definition.maxStack ?? 99,
      };
    }
    
    // Fallback to itemDefinitions prop (for inventory items)
    if (itemDefinitions[item.itemId]) {
      return itemDefinitions[item.itemId];
    }
    
    // Final fallback for items not in definitions
    return {
      id: item.itemId,
      name: item.itemId.replace(/_/g, ' ').toUpperCase(),
      description: 'Item',
      type: 'material',
      rarity: 'common',
      icon: '/assets/unknown.png',
      stackable: true,
      maxStack: 99,
    };
  };

  const renderInventoryGrid = (items: InventoryItem[], maxSlots: number, source: 'bank' | 'inventory') => {
    const slots: (InventoryItem | null)[] = [];
    
    // Fill with items
    for (const item of items) {
      slots.push(item);
    }
    
    // Fill remaining slots with null
    while (slots.length < maxSlots) {
      slots.push(null);
    }

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, 48px)`,
        gap: '6px',
        backgroundColor: '#161b22',
        padding: '8px',
        border: '1px solid #30363d',
        minHeight: '200px', // Minimum height for good UX
        maxHeight: '350px', // Maximum height to prevent overflow
        overflowY: 'auto',
        overflowX: 'hidden',
        justifyContent: 'start',
      }}>
        {slots.map((item, index) => {
          const def = getItemDefinition(item);
          const isSelected = selectedItem?.itemId === item?.itemId && selectedItem?.source === source;
          
          return (
            <div
              key={index}
              onClick={() => {
                if (item) {
                  setSelectedItem({ itemId: item.itemId, source });
                  if (source === 'inventory') {
                    setDepositQuantity(1);
                  } else {
                    setWithdrawQuantity(1);
                  }
                  setContextMenu(null);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                if (item) {
                  setContextMenu({
                    x: e.clientX,
                    y: e.clientY,
                    item,
                    source,
                  });
                }
              }}
              style={{
                width: '48px',
                height: '48px',
                border: `1px solid ${isSelected ? '#58a6ff' : def?.rarity ? RARITY_COLORS[def.rarity] || '#30363d' : '#30363d'}`,
                backgroundColor: isSelected ? '#21262d' : '#161b22',
                cursor: item ? 'pointer' : 'default',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0',
                position: 'relative',
                transition: 'all 0.15s ease',
                opacity: item ? 1 : 0.5,
              }}
              onMouseEnter={(e) => {
                if (item && !isSelected) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.borderColor = '#58a6ff';
                }
                if (item && def) {
                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                  let nextX: number = rect.right + 8;
                  let nextY: number = rect.bottom + 4;
                  
                  showTooltip(
                    {
                      title: def.name || 'Unknown Item',
                      type: def.type || 'item',
                      quantity: item.quantity,
                      rarity: def.rarity,
                      description: def.description,
                    },
                    nextX,
                    nextY
                  );
                }
              }}
              onMouseLeave={(e) => {
                if (item && !isSelected) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#161b22';
                  const def = getItemDefinition(item);
                  (e.currentTarget as HTMLElement).style.borderColor = def?.rarity ? RARITY_COLORS[def.rarity] || '#30363d' : '#30363d';
                }
                hideTooltip();
              }}
            >
              {item && def ? (
                <>
                  <img
                    src={(() => {
                      const publicUrl: string = process.env.PUBLIC_URL || '';
                      return def.icon && def.icon.startsWith('/assets')
                        ? `${publicUrl}${def.icon}`
                        : `${publicUrl}/assets/unknown.png`;
                    })()}
                    alt={def.name}
                    style={{ width: '80%', height: '80%', objectFit: 'contain', imageRendering: 'pixelated' }}
                    onError={(e) => {
                      const publicUrl: string = process.env.PUBLIC_URL || '';
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = `${publicUrl}/assets/unknown.png`;
                    }}
                  />
                  <span style={{
                    position: 'absolute',
                    bottom: '2px',
                    right: '2px',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    fontFamily: '"Sometype Mono", monospace',
                    color: '#ffffff',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                    padding: '1px 4px',
                    minWidth: '14px',
                    textAlign: 'center',
                    border: '1px solid #444',
                    lineHeight: '1',
                  }}>
                    {item.quantity}
                  </span>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  const selectedItemData = selectedItem 
    ? (selectedItem.source === 'bank' 
        ? bankItems.find(i => i.itemId === selectedItem.itemId)
        : inventoryItems.find(i => i.itemId === selectedItem.itemId))
    : null;
  const selectedDef = selectedItemData ? getItemDefinition(selectedItemData) : null;

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      title="MERCHANT'S VAULT"
      draggable
      storageKey="bankPanelPosition"
      defaultPosition={{ x: 150, y: 100 }}
      closeOnBackdrop={false}
      width={1000}
    >
      <div style={{
        width: '100%',
        padding: '12px',
        backgroundColor: '#0d1117',
        fontFamily: '"Sometype Mono", monospace',
        color: '#e6edf3',
      }}>
        {/* Subtitle */}
        <div style={{
          marginBottom: '12px',
          paddingBottom: '8px',
          borderBottom: '1px solid #21262d',
          textAlign: 'center',
        }}>
          <p style={{
            color: '#8b949e',
            margin: '0',
            fontSize: '12px',
            fontFamily: '"Sometype Mono", monospace',
          }}>
            Secure storage accessible at all official locations
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            marginBottom: '12px',
            padding: '8px',
            backgroundColor: 'rgba(248, 81, 73, 0.1)',
            border: '2px solid #f85149',
            color: '#f85149',
            fontSize: '11px',
          }}>
            {error}
          </div>
        )}

        {/* Quick action buttons */}
        <div style={{
          marginBottom: '12px',
          display: 'flex',
          gap: '8px',
          justifyContent: 'center',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={handleDepositInventory}
            disabled={inventoryItems.length === 0}
            style={{
              padding: '6px 12px',
              backgroundColor: inventoryItems.length === 0 ? '#21262d' : '#1f6feb',
              border: `2px solid ${inventoryItems.length === 0 ? '#30363d' : '#1f6feb'}`,
              color: inventoryItems.length === 0 ? '#8b949e' : '#fff',
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '10px',
              fontWeight: 'bold',
              cursor: inventoryItems.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (inventoryItems.length > 0) {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#388bfd';
              }
            }}
            onMouseLeave={(e) => {
              if (inventoryItems.length > 0) {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#1f6feb';
              }
            }}
          >
            DEPOSIT ALL
          </button>

          <button
            onClick={() => handleWithdrawAll()}
            disabled={bankItems.length === 0}
            style={{
              padding: '6px 12px',
              backgroundColor: bankItems.length === 0 ? '#21262d' : '#da3633',
              border: `2px solid ${bankItems.length === 0 ? '#30363d' : '#da3633'}`,
              color: bankItems.length === 0 ? '#8b949e' : '#fff',
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '10px',
              fontWeight: 'bold',
              cursor: bankItems.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (bankItems.length > 0) {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#f85149';
              }
            }}
            onMouseLeave={(e) => {
              if (bankItems.length > 0) {
                (e.currentTarget as HTMLElement).style.backgroundColor = '#da3633';
              }
            }}
          >
            WITHDRAW ALL
          </button>
        </div>

        {/* Main layout: Bank and Inventory side-by-side */}
        <div style={{
          marginBottom: '12px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px',
          minHeight: '300px',
          maxHeight: '400px', // Limit maximum height to prevent overflow
        }}>
          {/* Bank section */}
          <div style={{
            border: '1px solid #21262d',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
            <div style={{
              marginBottom: '8px',
              paddingBottom: '6px',
              borderBottom: '1px solid #21262d',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#79c0ff',
              flexShrink: 0,
            }}>
              BANK ({bankUsedSlots}/{bankMaxSlots})
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {renderInventoryGrid(bankItems, bankMaxSlots, 'bank')}
            </div>
          </div>

          {/* Inventory section */}
          <div style={{
            border: '1px solid #21262d',
            padding: '8px',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}>
            <div style={{
              marginBottom: '8px',
              paddingBottom: '6px',
              borderBottom: '1px solid #21262d',
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#79c0ff',
              flexShrink: 0,
            }}>
              INVENTORY ({inventoryUsedSlots}/{inventoryMaxSlots})
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {renderInventoryGrid(inventoryItems, inventoryMaxSlots, 'inventory')}
            </div>
          </div>
        </div>

        {/* Item details and controls */}
        {selectedDef && (
          <div style={{
            marginBottom: '12px',
            border: '1px solid #21262d',
            padding: '8px',
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
            }}>
              {/* Item info */}
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '8px',
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#0d1117',
                    border: '1px solid #30363d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {renderItemIcon(selectedDef.icon, selectedDef.name)}
                  </div>
                  <div>
                    <h4 style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#e6edf3',
                      margin: '0 0 2px 0',
                    }}>
                      {selectedDef.name}
                    </h4>
                    <p style={{
                      fontSize: '10px',
                      color: '#8b949e',
                      margin: '0',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      {selectedDef.type}
                    </p>
                  </div>
                </div>
                <p style={{
                  fontSize: '10px',
                  color: '#a0aec0',
                  margin: '0',
                  lineHeight: '1.4',
                }}>
                  {selectedDef.description}
                </p>
              </div>

              {/* Action controls */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                {selectedItem?.source === 'inventory' && (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <label style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: '#e6edf3',
                        whiteSpace: 'nowrap',
                      }}>
                        QUANTITY:
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={selectedItem ? (inventoryItems.find(i => i.itemId === selectedItem.itemId)?.quantity || 1) : 1}
                        value={depositQuantity}
                        onChange={(e) => setDepositQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{
                          width: '60px',
                          padding: '4px 6px',
                          backgroundColor: '#0d1117',
                          border: '2px solid #30363d',
                          color: '#e6edf3',
                          fontFamily: '"Sometype Mono", monospace',
                          fontSize: '11px',
                        }}
                      />
                    </div>
                    {/* Quick quantity buttons */}
                    <div style={{
                      display: 'flex',
                      gap: '4px',
                      flexWrap: 'wrap',
                    }}>
                      {[1, 5, 10, 'X', 'All'].map((qty) => (
                        <button
                          key={qty}
                          onClick={() => {
                            if (qty === 'X') {
                              // Keep current input value
                            } else {
                              handleQuantityButton(qty as number | 'all', 'deposit');
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: qty === 'X' ? '#21262d' : '#161b22',
                            border: '1px solid #30363d',
                            color: '#e6edf3',
                            fontFamily: '"Sometype Mono", monospace',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            flex: qty === 'X' ? '1 1 auto' : '0 0 auto',
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                            (e.currentTarget as HTMLElement).style.borderColor = '#58a6ff';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = qty === 'X' ? '#21262d' : '#161b22';
                            (e.currentTarget as HTMLElement).style.borderColor = '#30363d';
                          }}
                        >
                          {qty}
                        </button>
                      ))}
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                    }}>
                      <button
                        onClick={handleDeposit}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#1f6feb',
                          border: '2px solid #1f6feb',
                          color: '#fff',
                          fontFamily: '"Sometype Mono", monospace',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#388bfd';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#1f6feb';
                        }}
                      >
                        DEPOSIT
                      </button>
                      <button
                        onClick={handleDepositAll}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#238636',
                          border: '2px solid #238636',
                          color: '#fff',
                          fontFamily: '"Sometype Mono", monospace',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#2ea043';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#238636';
                        }}
                      >
                        DEPOSIT ALL
                      </button>
                    </div>
                  </>
                )}

                {selectedItem?.source === 'bank' && (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}>
                      <label style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                        color: '#e6edf3',
                        whiteSpace: 'nowrap',
                      }}>
                        QUANTITY:
                      </label>
                      <input
                        type="number"
                        min="1"
                        max={selectedItem ? (bankItems.find(i => i.itemId === selectedItem.itemId)?.quantity || 1) : 1}
                        value={withdrawQuantity}
                        onChange={(e) => setWithdrawQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                        style={{
                          width: '60px',
                          padding: '4px 6px',
                          backgroundColor: '#0d1117',
                          border: '2px solid #30363d',
                          color: '#e6edf3',
                          fontFamily: '"Sometype Mono", monospace',
                          fontSize: '11px',
                        }}
                      />
                    </div>
                    {/* Quick quantity buttons */}
                    <div style={{
                      display: 'flex',
                      gap: '4px',
                      flexWrap: 'wrap',
                    }}>
                      {[1, 5, 10, 'X', 'All'].map((qty) => (
                        <button
                          key={qty}
                          onClick={() => {
                            if (qty === 'X') {
                              // Keep current input value
                            } else {
                              handleQuantityButton(qty as number | 'all', 'withdraw');
                            }
                          }}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: qty === 'X' ? '#21262d' : '#161b22',
                            border: '1px solid #30363d',
                            color: '#e6edf3',
                            fontFamily: '"Sometype Mono", monospace',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            flex: qty === 'X' ? '1 1 auto' : '0 0 auto',
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                            (e.currentTarget as HTMLElement).style.borderColor = '#58a6ff';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.backgroundColor = qty === 'X' ? '#21262d' : '#161b22';
                            (e.currentTarget as HTMLElement).style.borderColor = '#30363d';
                          }}
                        >
                          {qty}
                        </button>
                      ))}
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                    }}>
                      <button
                        onClick={handleWithdraw}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#238636',
                          border: '2px solid #238636',
                          color: '#fff',
                          fontFamily: '"Sometype Mono", monospace',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#2ea043';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#238636';
                        }}
                      >
                        WITHDRAW
                      </button>
                      <button
                        onClick={() => handleWithdrawAll()}
                        style={{
                          flex: 1,
                          padding: '6px 12px',
                          backgroundColor: '#9c27b0',
                          border: '2px solid #9c27b0',
                          color: '#fff',
                          fontFamily: '"Sometype Mono", monospace',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#ab47bc';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.backgroundColor = '#9c27b0';
                        }}
                      >
                        WITHDRAW ALL
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Close button */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '6px 16px',
              backgroundColor: '#21262d',
              border: '2px solid #30363d',
              color: '#e6edf3',
              fontFamily: '"Sometype Mono", monospace',
              fontSize: '11px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = '#30363d';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
            }}
          >
            CLOSE
          </button>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            backgroundColor: '#0d1117',
            border: '1px solid #30363d',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            minWidth: '160px',
            fontFamily: '"Sometype Mono", monospace',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.source === 'inventory' && (
            <>
              <button
                onClick={() => {
                  setSelectedItem({ itemId: contextMenu.item.itemId, source: 'inventory' });
                  setDepositQuantity(1);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Deposit 1
              </button>
              <button
                onClick={() => {
                  setSelectedItem({ itemId: contextMenu.item.itemId, source: 'inventory' });
                  setDepositQuantity(5);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Deposit 5
              </button>
              <button
                onClick={() => {
                  setSelectedItem({ itemId: contextMenu.item.itemId, source: 'inventory' });
                  setDepositQuantity(10);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Deposit 10
              </button>
              <button
                onClick={() => {
                  setSelectedItem({ itemId: contextMenu.item.itemId, source: 'inventory' });
                  setDepositQuantity(contextMenu.item.quantity);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Deposit All
              </button>
            </>
          )}
          {contextMenu.source === 'bank' && (
            <>
              <button
                onClick={() => {
                  setSelectedItem({ itemId: contextMenu.item.itemId, source: 'bank' });
                  setWithdrawQuantity(1);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Withdraw 1
              </button>
              <button
                onClick={() => {
                  setSelectedItem({ itemId: contextMenu.item.itemId, source: 'bank' });
                  setWithdrawQuantity(5);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Withdraw 5
              </button>
              <button
                onClick={() => {
                  setSelectedItem({ itemId: contextMenu.item.itemId, source: 'bank' });
                  setWithdrawQuantity(10);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Withdraw 10
              </button>
              <button
                onClick={() => {
                  handleWithdrawAll(contextMenu.item.itemId);
                  setContextMenu(null);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'none',
                  border: 'none',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: '"Sometype Mono", monospace',
                  transition: 'background-color 150ms ease',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = '#21262d';
                  (e.currentTarget as HTMLElement).style.color = '#58a6ff';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = '#e2e8f0';
                }}
              >
                Withdraw All
              </button>
            </>
          )}
        </div>
      )}
    </OverlayPanel>
  );
};
